import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Avatar,
  Typography,
  Box,
  useTheme,
  MenuItem,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";

import { Strings } from "@app/constants";
import { AssignCustomerModal } from "@app/modules/salesperson/view/assign-customer-modal";
import { FormProvider, RHFSelect, RHFTextField } from "@app/components";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "@app/routes/paths";
import AssignSalesPersonModal from "@app/modules/requests/view/assign-salesperson";
import { useDispatch } from "react-redux";
import { soldCar } from "../slice";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedRow: any;
}

const SoldCarModal: React.FC<Props> = ({ open, onClose, selectedRow }) => {
  const theme = useTheme();
  const methods = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<any>(null);
  const [openCustomerSelect, setOpenCustomerSelect] = useState(false);
  const [openSalesPersonSelect, setOpenSalesPersonSelect] = useState(false);

  const fontFamily = Strings.fontLanguage.workSans;

  const onSubmit = (data: any) => {
    const newData = {
      ...data,
      carId: selectedRow?.id,
      customerId: selectedCustomer?.id,
      salesPersonId: 6,
    };

    console.log(newData, "datata");
    dispatch(soldCar(newData));

    onClose();
  };

  useEffect(() => {
    if (selectedRow?.price) {
      methods.setValue("finalPrice", selectedRow.price);
    }
  }, [selectedRow, methods]);

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      sx={{ py: 0.5 }}
    >
      <Typography
        variant="body1"
        sx={{ fontWeight: 600, color: "text.secondary", minWidth: "120px" }}
      >
        {label} :
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.primary",
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );

  return (
    <>
      {/* ------------------ Sold Car Modal ------------------ */}
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ pr: 2, pt: 1 }}
        >
          <DialogTitle
            sx={{
              fontFamily,
              fontWeight: 600,
              color: "#000",
            }}
          >
            Sell Car
          </DialogTitle>
          <Icon
            icon="material-symbols-light:close"
            width={24}
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </Box>

        <FormProvider methods={methods}>
          <DialogContent
            dividers
            sx={{ p: { xs: 2, sm: 3 }, maxHeight: "50vh", overflowY: "auto" }}
          >
            {/* -------- Select Customer Section -------- */}
            <Stack spacing={2}>
              <Typography fontWeight={600} fontFamily={fontFamily}>
                Customer Information
              </Typography>
              {selectedCustomer ? (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    p: 2,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    backgroundColor: theme.palette.grey[50],
                  }}
                >
                  <Avatar
                    src={selectedCustomer?.profileImage}
                    sx={{
                      width: 56,
                      height: 56,
                      border: `2px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    {selectedCustomer?.firstName?.[0] || "?"}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontFamily }}>
                      {selectedCustomer?.firstName} {selectedCustomer?.lastName}
                    </Typography>
                    <Typography variant="body2">
                      {selectedCustomer?.email}
                    </Typography>
                    <Typography variant="body2">
                      {selectedCustomer?.phone}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setOpenCustomerSelect(true)}
                    sx={{ ml: "auto" }}
                  >
                    Change
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => setOpenCustomerSelect(true)}
                  sx={{
                    alignSelf: "flex-start",
                    fontFamily,
                    fontWeight: 600,
                  }}
                >
                  Select Customer
                </Button>
              )}
              {/* ---------- Car Details ---------- */}
            </Stack>
            <Stack spacing={2} mt={2}>
              <Typography fontWeight={600} fontFamily={fontFamily}>
                Car Information
              </Typography>
              {selectedRow && (
                <Stack
                  sx={{
                    p: 2,
                    border: `1px solid grey`,
                    borderRadius: 2,
                  }}
                >
                  <InfoRow label="Car Id" value={selectedRow?.id} />
                  <InfoRow
                    label="Car Name"
                    value={`${selectedRow?.brand?.name} ${selectedRow?.model?.name}`}
                  />
                  <InfoRow label="Color" value={selectedRow?.color} />
                  <InfoRow label="Variant" value={selectedRow?.variant} />
                </Stack>
              )}
            </Stack>
            <Stack spacing={2} mt={2}>
              <Typography fontWeight={600} fontFamily={fontFamily}>
                Payment Information
              </Typography>
              <Stack sx={{ flexDirection: "column" }}>
                <Typography>Final Price</Typography>
                <RHFTextField name={"finalPrice"} placeholder="Final Price" />
              </Stack>
              <Stack sx={{ flexDirection: "column" }}>
                <Typography>Payment Method</Typography>
                <RHFSelect name={"paymentMethod"}>
                  <MenuItem value={"Cash"}>Cash</MenuItem>
                  <MenuItem value={"Cheque"}>Cheque</MenuItem>
                </RHFSelect>
              </Stack>
              <Stack sx={{ flexDirection: "column" }}>
                <Typography>Invoice Number</Typography>
                <RHFTextField
                  name={"invoiceNumber"}
                  placeholder="Invoice Number"
                />
              </Stack>
              <Stack sx={{ flexDirection: "column" }}>
                <Typography>Notes</Typography>
                <RHFTextField name={"notes"} placeholder="Notes" />
              </Stack>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              px: { xs: 2, sm: 3 },
              pb: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                width: { xs: "100%", sm: "28%" },
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                fontFamily,
                fontWeight: 700,
              }}
            >
              {Strings.button.close}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={methods.handleSubmit(onSubmit)}
              sx={{
                width: { xs: "100%", sm: "28%" },
                color: theme.palette.common.white,
                fontFamily,
                fontWeight: 700,
              }}
            >
              Proceed
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>

      {/* ------------------ Assign Customer Modal ------------------ */}
      <AssignCustomerModal
        open={openCustomerSelect}
        onClose={() => setOpenCustomerSelect(false)}
        selectedSalesPerson={selectedRow}
        onSelectCustomer={(customer: any) => {
          setSelectedCustomer(customer);
          setOpenCustomerSelect(false);
        }}
        isFromCar={true}
      />
    </>
  );
};

export { SoldCarModal };
